import * as z from 'zod';

export const UpdateCurrentMemberSlugRequest = z.object({
  slug: z.string().describe('New slug.').max(255),
});
export const UpdateCurrentMemberSlugResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Updated member.')
    .optional(),
});
export const UpdateMemberSlugRequest = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  slug: z.string().describe('New slug.').max(255),
});
export const UpdateMemberSlugResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Updated member.')
    .optional(),
});
export const JoinCommunityRequest = z.object({});
export const JoinCommunityResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('The updated member.')
    .optional(),
});
export const LeaveCommunityRequest = z.object({});
export const LeaveCommunityResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('The updated member.')
    .optional(),
});
export const GetCurrentMemberRequest = z.object({
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['PUBLIC', 'FULL', 'EXTENDED']))
        .max(3)
        .optional(),
    })
    .describe('Fieldset options.')
    .optional(),
});
export const GetCurrentMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('The retrieved member.')
    .optional(),
});
export const GetMemberRequest = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['PUBLIC', 'FULL', 'EXTENDED']))
        .max(3)
        .optional(),
    })
    .describe('Fieldset options.')
    .optional(),
});
export const GetMemberResponse = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  loginEmail: z
    .string()
    .describe('Email used by a member to log in to the site.')
    .email()
    .optional()
    .nullable(),
  loginEmailVerified: z
    .boolean()
    .describe('Whether the email used by a member has been verified.')
    .optional()
    .nullable(),
  status: z
    .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
    .describe('Member site access status.')
    .optional(),
  contactId: z
    .string()
    .describe('Contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  contact: z
    .object({
      firstName: z
        .string()
        .describe("Contact's first name.")
        .optional()
        .nullable(),
      lastName: z
        .string()
        .describe("Contact's last name.")
        .optional()
        .nullable(),
      phones: z.array(z.string()).optional(),
      emails: z.array(z.string()).optional(),
      addresses: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Street address ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              addressLine2: z
                .string()
                .describe(
                  'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                )
                .optional()
                .nullable(),
              city: z.string().describe('City name.').optional().nullable(),
              subdivision: z
                .string()
                .describe(
                  'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                )
                .optional()
                .nullable(),
              country: z
                .string()
                .describe(
                  '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                )
                .optional()
                .nullable(),
              postalCode: z
                .string()
                .describe('Postal code.')
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z.never().optional(),
              }),
              z.object({
                addressLine: z.never().optional(),
                streetAddress: z
                  .object({
                    number: z
                      .string()
                      .describe('Street number.')
                      .max(100)
                      .optional(),
                    name: z
                      .string()
                      .describe('Street name.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Street address object, with number and name in separate fields.'
                  ),
              }),
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z
                  .string()
                  .describe(
                    'Main address line, usually street and number, as free text.'
                  )
                  .max(200),
              }),
            ])
          )
        )
        .optional(),
      birthdate: z
        .string()
        .describe(
          'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
        )
        .max(100)
        .optional()
        .nullable(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(100)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(100)
        .optional()
        .nullable(),
      customFields: z
        .record(
          z.string(),
          z.object({
            name: z
              .string()
              .describe('Custom field name.')
              .optional()
              .nullable(),
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
          })
        )
        .describe(
          "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
        )
        .optional(),
    })
    .describe(
      "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
    )
    .optional(),
  profile: z
    .object({
      nickname: z
        .string()
        .describe(
          "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe("Slug that determines the member's profile page URL.")
        .optional()
        .nullable(),
      photo: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe("Member's profile photo.")
        .optional(),
      cover: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe(
          "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
        )
        .optional(),
      title: z.string().describe('Member title.').optional().nullable(),
    })
    .describe('Profile display details.')
    .optional(),
  privacyStatus: z
    .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
    .describe('Member privacy status.')
    .optional(),
  activityStatus: z
    .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
    .describe('Member activity status.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the member was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the member was updated.')
    .optional()
    .nullable(),
  lastLoginDate: z
    .date()
    .describe('Date and time when the member last logged in to the site.')
    .optional()
    .nullable(),
});
export const ListMembersRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .optional(),
      fieldsets: z
        .array(z.enum(['PUBLIC', 'FULL', 'EXTENDED']))
        .max(3)
        .optional(),
      sorting: z
        .array(
          z.object({
            fieldName: z
              .string()
              .describe('Name of the field to sort by.')
              .max(512)
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .describe('Options for paging, sorting, and specifying fields to return.')
    .optional(),
});
export const ListMembersResponse = z.object({
  members: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        loginEmail: z
          .string()
          .describe('Email used by a member to log in to the site.')
          .email()
          .optional()
          .nullable(),
        loginEmailVerified: z
          .boolean()
          .describe('Whether the email used by a member has been verified.')
          .optional()
          .nullable(),
        status: z
          .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
          .describe('Member site access status.')
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        contact: z
          .object({
            firstName: z
              .string()
              .describe("Contact's first name.")
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            phones: z.array(z.string()).optional(),
            emails: z.array(z.string()).optional(),
            addresses: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    addressLine2: z
                      .string()
                      .describe(
                        'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                      )
                      .optional()
                      .nullable(),
                    city: z
                      .string()
                      .describe('City name.')
                      .optional()
                      .nullable(),
                    subdivision: z
                      .string()
                      .describe(
                        'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                      )
                      .optional()
                      .nullable(),
                    country: z
                      .string()
                      .describe(
                        '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                      )
                      .optional()
                      .nullable(),
                    postalCode: z
                      .string()
                      .describe('Postal code.')
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z.never().optional(),
                    }),
                    z.object({
                      addressLine: z.never().optional(),
                      streetAddress: z
                        .object({
                          number: z
                            .string()
                            .describe('Street number.')
                            .max(100)
                            .optional(),
                          name: z
                            .string()
                            .describe('Street name.')
                            .max(200)
                            .optional(),
                        })
                        .describe(
                          'Street address object, with number and name in separate fields.'
                        ),
                    }),
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z
                        .string()
                        .describe(
                          'Main address line, usually street and number, as free text.'
                        )
                        .max(200),
                    }),
                  ])
                )
              )
              .optional(),
            birthdate: z
              .string()
              .describe(
                'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
              )
              .max(100)
              .optional()
              .nullable(),
            company: z
              .string()
              .describe("Contact's company name.")
              .max(100)
              .optional()
              .nullable(),
            jobTitle: z
              .string()
              .describe("Contact's job title.")
              .max(100)
              .optional()
              .nullable(),
            customFields: z
              .record(
                z.string(),
                z.object({
                  name: z
                    .string()
                    .describe('Custom field name.')
                    .optional()
                    .nullable(),
                  value: z
                    .any()
                    .describe('Custom field value.')
                    .optional()
                    .nullable(),
                })
              )
              .describe(
                "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
              )
              .optional(),
          })
          .describe(
            "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
          )
          .optional(),
        profile: z
          .object({
            nickname: z
              .string()
              .describe(
                "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
              )
              .optional()
              .nullable(),
            slug: z
              .string()
              .describe("Slug that determines the member's profile page URL.")
              .optional()
              .nullable(),
            photo: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                  )
                  .optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                offsetX: z
                  .number()
                  .int()
                  .describe('X-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
                offsetY: z
                  .number()
                  .int()
                  .describe('Y-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
              })
              .describe("Member's profile photo.")
              .optional(),
            cover: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                  )
                  .optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                offsetX: z
                  .number()
                  .int()
                  .describe('X-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
                offsetY: z
                  .number()
                  .int()
                  .describe('Y-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
              )
              .optional(),
            title: z.string().describe('Member title.').optional().nullable(),
          })
          .describe('Profile display details.')
          .optional(),
        privacyStatus: z
          .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
          .describe('Member privacy status.')
          .optional(),
        activityStatus: z
          .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
          .describe('Member activity status.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time when the member was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time when the member was updated.')
          .optional()
          .nullable(),
        lastLoginDate: z
          .date()
          .describe('Date and time when the member last logged in to the site.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const QueryMembersRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'profile.nickname': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'profile.slug': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          contactId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'contact.firstName': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'contact.lastName': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          privacyStatus: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          loginEmail: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z.array(z.object({})).optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['PUBLIC', 'FULL', 'EXTENDED']))
        .max(3)
        .optional(),
      search: z
        .object({
          expression: z
            .string()
            .describe('Search term or expression.')
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(4).optional(),
        })
        .describe('Plain text search.')
        .optional(),
    })
    .describe('Query options.')
    .optional(),
});
export const QueryMembersResponse = z.object({
  members: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        loginEmail: z
          .string()
          .describe('Email used by a member to log in to the site.')
          .email()
          .optional()
          .nullable(),
        loginEmailVerified: z
          .boolean()
          .describe('Whether the email used by a member has been verified.')
          .optional()
          .nullable(),
        status: z
          .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
          .describe('Member site access status.')
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        contact: z
          .object({
            firstName: z
              .string()
              .describe("Contact's first name.")
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            phones: z.array(z.string()).optional(),
            emails: z.array(z.string()).optional(),
            addresses: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    addressLine2: z
                      .string()
                      .describe(
                        'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                      )
                      .optional()
                      .nullable(),
                    city: z
                      .string()
                      .describe('City name.')
                      .optional()
                      .nullable(),
                    subdivision: z
                      .string()
                      .describe(
                        'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                      )
                      .optional()
                      .nullable(),
                    country: z
                      .string()
                      .describe(
                        '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                      )
                      .optional()
                      .nullable(),
                    postalCode: z
                      .string()
                      .describe('Postal code.')
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z.never().optional(),
                    }),
                    z.object({
                      addressLine: z.never().optional(),
                      streetAddress: z
                        .object({
                          number: z
                            .string()
                            .describe('Street number.')
                            .max(100)
                            .optional(),
                          name: z
                            .string()
                            .describe('Street name.')
                            .max(200)
                            .optional(),
                        })
                        .describe(
                          'Street address object, with number and name in separate fields.'
                        ),
                    }),
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z
                        .string()
                        .describe(
                          'Main address line, usually street and number, as free text.'
                        )
                        .max(200),
                    }),
                  ])
                )
              )
              .optional(),
            birthdate: z
              .string()
              .describe(
                'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
              )
              .max(100)
              .optional()
              .nullable(),
            company: z
              .string()
              .describe("Contact's company name.")
              .max(100)
              .optional()
              .nullable(),
            jobTitle: z
              .string()
              .describe("Contact's job title.")
              .max(100)
              .optional()
              .nullable(),
            customFields: z
              .record(
                z.string(),
                z.object({
                  name: z
                    .string()
                    .describe('Custom field name.')
                    .optional()
                    .nullable(),
                  value: z
                    .any()
                    .describe('Custom field value.')
                    .optional()
                    .nullable(),
                })
              )
              .describe(
                "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
              )
              .optional(),
          })
          .describe(
            "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
          )
          .optional(),
        profile: z
          .object({
            nickname: z
              .string()
              .describe(
                "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
              )
              .optional()
              .nullable(),
            slug: z
              .string()
              .describe("Slug that determines the member's profile page URL.")
              .optional()
              .nullable(),
            photo: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                  )
                  .optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                offsetX: z
                  .number()
                  .int()
                  .describe('X-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
                offsetY: z
                  .number()
                  .int()
                  .describe('Y-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
              })
              .describe("Member's profile photo.")
              .optional(),
            cover: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                  )
                  .optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                offsetX: z
                  .number()
                  .int()
                  .describe('X-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
                offsetY: z
                  .number()
                  .int()
                  .describe('Y-axis offset.\n\nDefault: `0`.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
              )
              .optional(),
            title: z.string().describe('Member title.').optional().nullable(),
          })
          .describe('Profile display details.')
          .optional(),
        privacyStatus: z
          .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
          .describe('Member privacy status.')
          .optional(),
        activityStatus: z
          .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
          .describe('Member activity status.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time when the member was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time when the member was updated.')
          .optional()
          .nullable(),
        lastLoginDate: z
          .date()
          .describe('Date and time when the member last logged in to the site.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const MuteMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member to mute.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const MuteMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Muted member.')
    .optional(),
});
export const UnmuteMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member to unmute.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const UnmuteMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Unmuted member.')
    .optional(),
});
export const ApproveMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member to approve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ApproveMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Approved member.')
    .optional(),
});
export const BlockMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of a member to block.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const BlockMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Blocked member.')
    .optional(),
});
export const DisconnectMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of a member to disconnect.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DisconnectMemberResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Disconnected member.')
    .optional(),
});
export const DeleteMemberRequest = z.object({
  _id: z
    .string()
    .describe('ID of a member to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteMemberResponse = z.object({});
export const DeleteMyMemberRequest = z.object({
  options: z
    .object({
      contentAssigneeId: z
        .string()
        .describe("ID of a member receiving the deleted member's content.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const DeleteMyMemberResponse = z.object({});
export const BulkDeleteMembersRequest = z.object({
  memberIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteMembersResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .max(255)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action result metadata.')
    .optional(),
});
export const BulkDeleteMembersByFilterRequest = z.object({
  filter: z
    .any()
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.'
    ),
  options: z
    .object({
      contentAssigneeId: z
        .string()
        .describe("ID of a member receiving the deleted member's content.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      search: z
        .object({
          expression: z
            .string()
            .describe('Search term or expression.')
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(4).optional(),
        })
        .describe('Plain text search.')
        .optional(),
    })
    .optional(),
});
export const BulkDeleteMembersByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\nSpecify this ID when calling [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .optional(),
});
export const BulkApproveMembersRequest = z.object({
  filter: z
    .any()
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.'
    ),
});
export const BulkApproveMembersResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\nSpecify this ID when calling [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .optional(),
});
export const BulkBlockMembersRequest = z.object({
  filter: z
    .any()
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.'
    ),
});
export const BulkBlockMembersResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\nSpecify this ID when calling [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .optional(),
});
export const CreateMemberRequest = z.object({
  options: z
    .object({
      member: z
        .object({
          _id: z
            .string()
            .describe('Member ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          loginEmail: z
            .string()
            .describe('Email used by a member to log in to the site.')
            .email()
            .optional()
            .nullable(),
          loginEmailVerified: z
            .boolean()
            .describe('Whether the email used by a member has been verified.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
            .optional(),
          contactId: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          contact: z
            .object({
              firstName: z
                .string()
                .describe("Contact's first name.")
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe("Contact's last name.")
                .optional()
                .nullable(),
              phones: z.array(z.string()).optional(),
              emails: z.array(z.string()).optional(),
              addresses: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Street address ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      addressLine2: z
                        .string()
                        .describe(
                          'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                        )
                        .optional()
                        .nullable(),
                      city: z
                        .string()
                        .describe('City name.')
                        .optional()
                        .nullable(),
                      subdivision: z
                        .string()
                        .describe(
                          'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                        )
                        .optional()
                        .nullable(),
                      country: z
                        .string()
                        .describe(
                          '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                        )
                        .optional()
                        .nullable(),
                      postalCode: z
                        .string()
                        .describe('Postal code.')
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        streetAddress: z.never().optional(),
                        addressLine: z.never().optional(),
                      }),
                      z.object({
                        addressLine: z.never().optional(),
                        streetAddress: z
                          .object({
                            number: z
                              .string()
                              .describe('Street number.')
                              .max(100)
                              .optional(),
                            name: z
                              .string()
                              .describe('Street name.')
                              .max(200)
                              .optional(),
                          })
                          .describe(
                            'Street address object, with number and name in separate fields.'
                          ),
                      }),
                      z.object({
                        streetAddress: z.never().optional(),
                        addressLine: z
                          .string()
                          .describe(
                            'Main address line, usually street and number, as free text.'
                          )
                          .max(200),
                      }),
                    ])
                  )
                )
                .optional(),
              birthdate: z
                .string()
                .describe(
                  'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
                )
                .max(100)
                .optional()
                .nullable(),
              company: z
                .string()
                .describe("Contact's company name.")
                .max(100)
                .optional()
                .nullable(),
              jobTitle: z
                .string()
                .describe("Contact's job title.")
                .max(100)
                .optional()
                .nullable(),
              customFields: z
                .record(
                  z.string(),
                  z.object({
                    name: z
                      .string()
                      .describe('Custom field name.')
                      .optional()
                      .nullable(),
                    value: z
                      .any()
                      .describe('Custom field value.')
                      .optional()
                      .nullable(),
                  })
                )
                .describe(
                  "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
                )
                .optional(),
            })
            .describe(
              "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
            )
            .optional(),
          profile: z
            .object({
              nickname: z
                .string()
                .describe(
                  "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
                )
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe("Slug that determines the member's profile page URL.")
                .optional()
                .nullable(),
              photo: z
                .object({
                  _id: z
                    .string()
                    .describe(
                      'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                    )
                    .optional(),
                  url: z.string().describe('Image URL.').optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Original image width.')
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Original image height.')
                    .optional(),
                  offsetX: z
                    .number()
                    .int()
                    .describe('X-axis offset.\n\nDefault: `0`.')
                    .optional()
                    .nullable(),
                  offsetY: z
                    .number()
                    .int()
                    .describe('Y-axis offset.\n\nDefault: `0`.')
                    .optional()
                    .nullable(),
                })
                .describe("Member's profile photo.")
                .optional(),
              cover: z
                .object({
                  _id: z
                    .string()
                    .describe(
                      'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                    )
                    .optional(),
                  url: z.string().describe('Image URL.').optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Original image width.')
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Original image height.')
                    .optional(),
                  offsetX: z
                    .number()
                    .int()
                    .describe('X-axis offset.\n\nDefault: `0`.')
                    .optional()
                    .nullable(),
                  offsetY: z
                    .number()
                    .int()
                    .describe('Y-axis offset.\n\nDefault: `0`.')
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
                )
                .optional(),
              title: z.string().describe('Member title.').optional().nullable(),
            })
            .describe('Profile display details.')
            .optional(),
          privacyStatus: z.enum(['UNKNOWN', 'PRIVATE', 'PUBLIC']).optional(),
          activityStatus: z.enum(['UNKNOWN', 'ACTIVE', 'MUTED']).optional(),
          _createdDate: z
            .date()
            .describe('Date and time when the member was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time when the member was updated.')
            .optional()
            .nullable(),
          lastLoginDate: z
            .date()
            .describe(
              'Date and time when the member last logged in to the site.'
            )
            .optional()
            .nullable(),
        })
        .describe('Member to create.')
        .optional(),
    })
    .optional(),
});
export const CreateMemberResponse = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  loginEmail: z
    .string()
    .describe('Email used by a member to log in to the site.')
    .email()
    .optional()
    .nullable(),
  loginEmailVerified: z
    .boolean()
    .describe('Whether the email used by a member has been verified.')
    .optional()
    .nullable(),
  status: z
    .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
    .describe('Member site access status.')
    .optional(),
  contactId: z
    .string()
    .describe('Contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  contact: z
    .object({
      firstName: z
        .string()
        .describe("Contact's first name.")
        .optional()
        .nullable(),
      lastName: z
        .string()
        .describe("Contact's last name.")
        .optional()
        .nullable(),
      phones: z.array(z.string()).optional(),
      emails: z.array(z.string()).optional(),
      addresses: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Street address ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              addressLine2: z
                .string()
                .describe(
                  'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                )
                .optional()
                .nullable(),
              city: z.string().describe('City name.').optional().nullable(),
              subdivision: z
                .string()
                .describe(
                  'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                )
                .optional()
                .nullable(),
              country: z
                .string()
                .describe(
                  '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                )
                .optional()
                .nullable(),
              postalCode: z
                .string()
                .describe('Postal code.')
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z.never().optional(),
              }),
              z.object({
                addressLine: z.never().optional(),
                streetAddress: z
                  .object({
                    number: z
                      .string()
                      .describe('Street number.')
                      .max(100)
                      .optional(),
                    name: z
                      .string()
                      .describe('Street name.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Street address object, with number and name in separate fields.'
                  ),
              }),
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z
                  .string()
                  .describe(
                    'Main address line, usually street and number, as free text.'
                  )
                  .max(200),
              }),
            ])
          )
        )
        .optional(),
      birthdate: z
        .string()
        .describe(
          'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
        )
        .max(100)
        .optional()
        .nullable(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(100)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(100)
        .optional()
        .nullable(),
      customFields: z
        .record(
          z.string(),
          z.object({
            name: z
              .string()
              .describe('Custom field name.')
              .optional()
              .nullable(),
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
          })
        )
        .describe(
          "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
        )
        .optional(),
    })
    .describe(
      "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
    )
    .optional(),
  profile: z
    .object({
      nickname: z
        .string()
        .describe(
          "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe("Slug that determines the member's profile page URL.")
        .optional()
        .nullable(),
      photo: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe("Member's profile photo.")
        .optional(),
      cover: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe(
          "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
        )
        .optional(),
      title: z.string().describe('Member title.').optional().nullable(),
    })
    .describe('Profile display details.')
    .optional(),
  privacyStatus: z
    .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
    .describe('Member privacy status.')
    .optional(),
  activityStatus: z
    .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
    .describe('Member activity status.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the member was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the member was updated.')
    .optional()
    .nullable(),
  lastLoginDate: z
    .date()
    .describe('Date and time when the member last logged in to the site.')
    .optional()
    .nullable(),
});
export const UpdateMemberRequest = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z.enum(['UNKNOWN', 'PRIVATE', 'PUBLIC']).optional(),
      activityStatus: z.enum(['UNKNOWN', 'ACTIVE', 'MUTED']).optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Member info to update.'),
});
export const UpdateMemberResponse = z.object({
  _id: z
    .string()
    .describe('Member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  loginEmail: z
    .string()
    .describe('Email used by a member to log in to the site.')
    .email()
    .optional()
    .nullable(),
  loginEmailVerified: z
    .boolean()
    .describe('Whether the email used by a member has been verified.')
    .optional()
    .nullable(),
  status: z
    .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
    .describe('Member site access status.')
    .optional(),
  contactId: z
    .string()
    .describe('Contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  contact: z
    .object({
      firstName: z
        .string()
        .describe("Contact's first name.")
        .optional()
        .nullable(),
      lastName: z
        .string()
        .describe("Contact's last name.")
        .optional()
        .nullable(),
      phones: z.array(z.string()).optional(),
      emails: z.array(z.string()).optional(),
      addresses: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Street address ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              addressLine2: z
                .string()
                .describe(
                  'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                )
                .optional()
                .nullable(),
              city: z.string().describe('City name.').optional().nullable(),
              subdivision: z
                .string()
                .describe(
                  'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                )
                .optional()
                .nullable(),
              country: z
                .string()
                .describe(
                  '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                )
                .optional()
                .nullable(),
              postalCode: z
                .string()
                .describe('Postal code.')
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z.never().optional(),
              }),
              z.object({
                addressLine: z.never().optional(),
                streetAddress: z
                  .object({
                    number: z
                      .string()
                      .describe('Street number.')
                      .max(100)
                      .optional(),
                    name: z
                      .string()
                      .describe('Street name.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Street address object, with number and name in separate fields.'
                  ),
              }),
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z
                  .string()
                  .describe(
                    'Main address line, usually street and number, as free text.'
                  )
                  .max(200),
              }),
            ])
          )
        )
        .optional(),
      birthdate: z
        .string()
        .describe(
          'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
        )
        .max(100)
        .optional()
        .nullable(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(100)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(100)
        .optional()
        .nullable(),
      customFields: z
        .record(
          z.string(),
          z.object({
            name: z
              .string()
              .describe('Custom field name.')
              .optional()
              .nullable(),
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
          })
        )
        .describe(
          "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
        )
        .optional(),
    })
    .describe(
      "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
    )
    .optional(),
  profile: z
    .object({
      nickname: z
        .string()
        .describe(
          "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe("Slug that determines the member's profile page URL.")
        .optional()
        .nullable(),
      photo: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe("Member's profile photo.")
        .optional(),
      cover: z
        .object({
          _id: z
            .string()
            .describe(
              'Wix Media image ID,\nset when the member selects an image from Wix Media.'
            )
            .optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z.number().int().describe('Original image width.').optional(),
          width: z.number().int().describe('Original image height.').optional(),
          offsetX: z
            .number()
            .int()
            .describe('X-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
          offsetY: z
            .number()
            .int()
            .describe('Y-axis offset.\n\nDefault: `0`.')
            .optional()
            .nullable(),
        })
        .describe(
          "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
        )
        .optional(),
      title: z.string().describe('Member title.').optional().nullable(),
    })
    .describe('Profile display details.')
    .optional(),
  privacyStatus: z
    .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
    .describe('Member privacy status.')
    .optional(),
  activityStatus: z
    .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
    .describe('Member activity status.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the member was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the member was updated.')
    .optional()
    .nullable(),
  lastLoginDate: z
    .date()
    .describe('Date and time when the member last logged in to the site.')
    .optional()
    .nullable(),
});
export const DeleteMemberPhonesRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member whose phone numbers will be deleted.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteMemberPhonesResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Updated member.')
    .optional(),
});
export const DeleteMemberEmailsRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member whose email addresses will be deleted.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteMemberEmailsResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Updated member.')
    .optional(),
});
export const DeleteMemberAddressesRequest = z.object({
  _id: z
    .string()
    .describe('ID of the member whose street addresses will be deleted.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteMemberAddressesResponse = z.object({
  member: z
    .object({
      _id: z
        .string()
        .describe('Member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      loginEmail: z
        .string()
        .describe('Email used by a member to log in to the site.')
        .email()
        .optional()
        .nullable(),
      loginEmailVerified: z
        .boolean()
        .describe('Whether the email used by a member has been verified.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'APPROVED', 'BLOCKED', 'OFFLINE'])
        .describe('Member site access status.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      contact: z
        .object({
          firstName: z
            .string()
            .describe("Contact's first name.")
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Contact's last name.")
            .optional()
            .nullable(),
          phones: z.array(z.string()).optional(),
          emails: z.array(z.string()).optional(),
          addresses: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Street address ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address information,\nsuch as apartment, suite, or floor.'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Code for a subdivision (such as state, prefecture, or province) in an\n[ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.'
                    )
                    .optional()
                    .nullable(),
                  country: z
                    .string()
                    .describe(
                      '2-letter country code in an\n[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                    )
                    .optional()
                    .nullable(),
                  postalCode: z
                    .string()
                    .describe('Postal code.')
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .max(100)
                          .optional(),
                        name: z
                          .string()
                          .describe('Street name.')
                          .max(200)
                          .optional(),
                      })
                      .describe(
                        'Street address object, with number and name in separate fields.'
                      ),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      )
                      .max(200),
                  }),
                ])
              )
            )
            .optional(),
          birthdate: z
            .string()
            .describe(
              'Contact\'s birthdate, formatted as `"YYYY-MM-DD"`.\n\nExample: `"2020-03-15"` for March 15, 2020.'
            )
            .max(100)
            .optional()
            .nullable(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(100)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(100)
            .optional()
            .nullable(),
          customFields: z
            .record(
              z.string(),
              z.object({
                name: z
                  .string()
                  .describe('Custom field name.')
                  .optional()
                  .nullable(),
                value: z
                  .any()
                  .describe('Custom field value.')
                  .optional()
                  .nullable(),
              })
            )
            .describe(
              "Custom fields,\nwhere each key is the field key,\nand each value is the field's value for the member."
            )
            .optional(),
        })
        .describe(
          "Member's contact information. Contact information is stored in the\n[Contact List](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fcontacts).\n\nThe full set of contact data can be accessed and managed with the\n[Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction)."
        )
        .optional(),
      profile: z
        .object({
          nickname: z
            .string()
            .describe(
              "Name that identifies the member to other members.\nDisplayed on the member's profile page\nand interactions in the forum or blog."
            )
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe("Slug that determines the member's profile page URL.")
            .optional()
            .nullable(),
          photo: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe("Member's profile photo.")
            .optional(),
          cover: z
            .object({
              _id: z
                .string()
                .describe(
                  'Wix Media image ID,\nset when the member selects an image from Wix Media.'
                )
                .optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              offsetX: z
                .number()
                .int()
                .describe('X-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
              offsetY: z
                .number()
                .int()
                .describe('Y-axis offset.\n\nDefault: `0`.')
                .optional()
                .nullable(),
            })
            .describe(
              "Member's cover photo,\nused as a background picture in a member's profile page.\n\nCover positioning can be altered with `cover.offsetX` and `cover.offsetY`.\nWhen left empty, the values default to `0`."
            )
            .optional(),
          title: z.string().describe('Member title.').optional().nullable(),
        })
        .describe('Profile display details.')
        .optional(),
      privacyStatus: z
        .enum(['UNKNOWN', 'PRIVATE', 'PUBLIC'])
        .describe('Member privacy status.')
        .optional(),
      activityStatus: z
        .enum(['UNKNOWN', 'ACTIVE', 'MUTED'])
        .describe('Member activity status.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the member was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the member was updated.')
        .optional()
        .nullable(),
      lastLoginDate: z
        .date()
        .describe('Date and time when the member last logged in to the site.')
        .optional()
        .nullable(),
    })
    .describe('Updated member.')
    .optional(),
});
